import {FC, useEffect, useState, useMemo, memo, useCallback, useRef, ReactNode, lazy} from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Nullable, Project, API, PageModel } from '@brg/api';
import { useProfile, bProfile } from '@brg/auth';
import { FlexBox, useBreakpoint } from '@brg/ui';
import { ProjectsGridNew } from '../../shared/components/grids';
import ProjectCardNew from '../../shared/components/projectCard/projectCardNew';
import { computeProject } from '../../shared/helpers/types/computed/ComputedProject';
import { bProject } from '../utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AppTester } from "supporting";
import { BookCard } from '_deziiign/books/core/components/bookCard';
import { computeBook, computeBookProject } from '_deziiign/books/core/components/helper/types/computed/ComputedBook';
import { EmptyCardsLoading } from 'shared/components/CardsLoading/EmptyCards';
import { ProjectDelegate } from 'project-delegate';
import { TOwnerRights } from 'shared/pages/exploreHseContextsNew/exploreHseContextsNew';
import { CombinedEvents } from "../../shared/helpers/projectEvents";
import { ModerationType } from "../../shared/pages/teams/const/ModerationTypes";
import {BookDelegate} from "../../_deziiign/books/core/book-delegate";
import { LinkTo } from 'shared/constants/linkTo';
import { useParams } from "react-router-dom";
import { isServer } from 'shared/constants/isServer';
import { useParamsSsr } from 'shared/hooks/useRouteMatchSsr';
import { fetchFolderData } from 'shared/actions/folderProjectsActions';
import ProfileFoldersSlice, { ProfileFoldersSelector } from 'shared/reducers/ProfileFoldersSlice';
import { FoldersMove } from 'shared/components/FoldersMove/FoldersMove';
import { LayoutConst } from 'core/modules';
import isEqual from "lodash.isequal";

const usedUseParams = isServer ? useParamsSsr : useParams
const useParamsProps = isServer ? LinkTo.designer : undefined

export const StyledLoading = styled.div`
  margin-bottom: 40px;
  padding: 10px;
  font-size: 60px;
  line-height: 1;
  color: white;
  text-align: center;
`;

export const ShowMoreWrapper = styled(FlexBox)`
  margin-left: auto;
  margin-right: auto;
  border: 1px solid ${p => p.theme.progressbar.borderColor};
  border-radius: 3px;
  padding: 12px 48px;
`;

const OffsetBlock = styled.div<{ display: boolean }>`
  width: 100%;
  margin-top: 64px;
  display: ${p => p?.display ? 'inline-block' : 'none'};
`;

type Tabs = {
  [key: string]: string[] | string | null;
}

export interface ProjectsFolderControllerProps<T = Project> {
  filter: any;
  onProjectsLoaded?: (projects: T[]) => void;
  onDragProjects: (data: Project[], test: any) => void;
  selectable?: boolean;
  onSelect?: (projectId: string) => void;
  withoutGoToProject?: boolean;
  isParticipate?: string[];
  modal?: boolean;
  linkPrefix?: string;
  projectsCount?: number;
  isLoading?: Function;
  isSelectRightNeed?: boolean;
  columns?: number;
  isUnload?: boolean;
  preloadedTabs?: Tabs[];
  emptyMsgComponent?: ReactNode;
  currentTab: string;
  currentCategory: string;
  contextName?: string;
  isPageWithoutTabs?: boolean;
  emptyMsgText?: string;
  showCompetitionPoint?: boolean;
  isTeamProjects?: boolean;
  isDisabled?: boolean;
  canMakeImageForTeam?: TOwnerRights;
  isMainPage?: boolean;
  isProfile?: boolean;
  isPeoplePage?: boolean;
  isCompetitions?: boolean;
  handlers?: CombinedEvents;
  moderationType?: ModerationType;
  loading?: boolean;
  teamId?: string;
  mobileColumns?: number;
  scrollableTarget?: any;
  isMe?: number;
  scrollStyles?: any;
  showOpa?: boolean;
  isOpaCard?: boolean;
  isMobileMoreVisible?: boolean;

  folderId: string;
  canDrag?: boolean,
}

const ProjectsFolderController: FC<ProjectsFolderControllerProps> = ({
  filter,
  modal = false,
  onDragProjects,
  withoutGoToProject,
  linkPrefix,
  columns,
  showCompetitionPoint,
  canMakeImageForTeam,
  isProfile,
  isMainPage,
  handlers,
  mobileColumns,
  loading,
  folderId,
  canDrag,
}) => {
  const designer = useProfile();
  const { id: designerId } = usedUseParams(useParamsProps)
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
  const dispatch = useDispatch();
  const isMedia = AppTester.isMediiia();

  const { i18n: { language } } = useTranslation();
  const [time, setTime] = useState(Date.now());

  const prevUserId = useRef<any>(null);
  const prevFilterRef = useRef<any>(null);

  const data = useSelector(ProfileFoldersSelector)?.data as PageModel<Project>;
  const fetchingTabs = useSelector(ProfileFoldersSelector)?.isLoading;
  const prevFolder = useSelector(ProfileFoldersSelector)?.prevFolder;

  const reloadAllTabs = useCallback(() => {
    setTime(Date.now());
    handlers?.onUpdate?.();
  }, [handlers])

  const equalRequest = useMemo(() => {
    return !!isEqual((filter?.projectIds || []), prevFilterRef.current)
  } , [filter, filter?.projectIds, prevFilterRef])

  useEffect(() => {
    if (isProfile && prevUserId.current && prevUserId.current !== designerId) {
      dispatch(ProfileFoldersSlice.actions.resetFolderProjects())
    }

    prevUserId.current = designerId
  } , [prevUserId, designerId, dispatch, isProfile])

  useEffect(() => {
    if (!equalRequest) {
      dispatch(fetchFolderData({ filter }))
    }

    prevFilterRef.current = filter?.projectIds

  }, [
    time,
    prevFilterRef,
    equalRequest,
    prevFolder,
    folderId,
    filter,
  ])

  useEffect(() => {
    return () => {
      dispatch(ProfileFoldersSlice.actions.setPrevFolder(folderId))
      dispatch(ProfileFoldersSlice.actions.resetFolderProjects())
    }
  }, [folderId])

  const translatedProjects = useMemo(() => {
    return data?.data ? API.toLang<Project[]>(data?.data, language) : []
  }, [data?.data, language]);

  return (
    <FlexBox offsetTop={24} offsetBottom={50} className={'folder-grid'} direction="column" directionMobile="column" gy={isMedia ? 0 : 40} flex="1">

        {/*@ts-ignore*/}
       {canDrag && !isMobile ? (
        <FoldersMove 
          projects={translatedProjects}
          // @ts-ignore
          onDragProjects={onDragProjects}
          canDrag={canDrag}
       />
       ) : (
        <ProjectsGridNew k={isMainPage ? 20 : undefined} isLoading={fetchingTabs} columns={columns} mobileColumns={mobileColumns}>
          {translatedProjects?.map((v: Nullable<Partial<Project<'Local'>>>, index: number) => {
            if (!v?.isBook) {
              return (
                // @ts-ignore
                <ProjectCardNew
                  project={v as any}
                  isModal={modal}
                  withoutGoToProject={withoutGoToProject}
                  onProjectClick={modal ?
                    (projectId?: string, e?: any) => {
                      e?.stopPropagation();
                      e?.preventDefault();
                    } : undefined}
                  isEditable={
                    ((v && designer && bProject.isEditableFor(v as any, designer)) ||
                      bProfile.isModer(designer as any) || bProfile.isMiddleModer(designer as any))
                      && !BookDelegate?.isPaid(v as any)
                  }
                  //@ts-ignore
                  isRejected={ProjectDelegate.isRejectedProject(v)}
                  key={(v?.projectId || '') + index}
                  {...computeProject(v)}
                  onShouldReload={reloadAllTabs}
                  linkPrefix={linkPrefix ? linkPrefix : undefined}
                  showCompetitionPoint={showCompetitionPoint}
                  canMakeImageForTeam={canMakeImageForTeam}
                />
              );
            } else {
              const book = computeBookProject(v);
              return (
                // @ts-ignore
                <BookCard
                  book={book as any}
                  key={(book?.bookId || '') + index}
                  {...computeBook(book)}
                  onShouldReload={reloadAllTabs}
                  linkPrefix={linkPrefix ? linkPrefix : undefined}
                />
              );
            }
          })}
        </ProjectsGridNew>
       )}
      <EmptyCardsLoading k={isMainPage ? 0 : 8} isLoading={fetchingTabs}/>
      <OffsetBlock display={true}/>
    </FlexBox>
  );
};

export default memo(ProjectsFolderController)import {FC, useEffect, useState, useMemo, memo, useCallback, useRef, ReactNode, lazy} from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Nullable, Project, API, PageModel } from '@brg/api';
import { useProfile, bProfile } from '@brg/auth';
import { FlexBox, useBreakpoint } from '@brg/ui';
import { ProjectsGridNew } from '../../shared/components/grids';
import ProjectCardNew from '../../shared/components/projectCard/projectCardNew';
import { computeProject } from '../../shared/helpers/types/computed/ComputedProject';
import { bProject } from '../utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AppTester } from "supporting";
import { BookCard } from '_deziiign/books/core/components/bookCard';
import { computeBook, computeBookProject } from '_deziiign/books/core/components/helper/types/computed/ComputedBook';
import { EmptyCardsLoading } from 'shared/components/CardsLoading/EmptyCards';
import { ProjectDelegate } from 'project-delegate';
import { TOwnerRights } from 'shared/pages/exploreHseContextsNew/exploreHseContextsNew';
import { CombinedEvents } from "../../shared/helpers/projectEvents";
import { ModerationType } from "../../shared/pages/teams/const/ModerationTypes";
import {BookDelegate} from "../../_deziiign/books/core/book-delegate";
import { LinkTo } from 'shared/constants/linkTo';
import { useParams } from "react-router-dom";
import { isServer } from 'shared/constants/isServer';
import { useParamsSsr } from 'shared/hooks/useRouteMatchSsr';
import { fetchFolderData } from 'shared/actions/folderProjectsActions';
import ProfileFoldersSlice, { ProfileFoldersSelector } from 'shared/reducers/ProfileFoldersSlice';
import { FoldersMove } from 'shared/components/FoldersMove/FoldersMove';
import { LayoutConst } from 'core/modules';
import isEqual from "lodash.isequal";

const usedUseParams = isServer ? useParamsSsr : useParams
const useParamsProps = isServer ? LinkTo.designer : undefined

export const StyledLoading = styled.div`
  margin-bottom: 40px;
  padding: 10px;
  font-size: 60px;
  line-height: 1;
  color: white;
  text-align: center;
`;

export const ShowMoreWrapper = styled(FlexBox)`
  margin-left: auto;
  margin-right: auto;
  border: 1px solid ${p => p.theme.progressbar.borderColor};
  border-radius: 3px;
  padding: 12px 48px;
`;

const OffsetBlock = styled.div<{ display: boolean }>`
  width: 100%;
  margin-top: 64px;
  display: ${p => p?.display ? 'inline-block' : 'none'};
`;

type Tabs = {
  [key: string]: string[] | string | null;
}

export interface ProjectsFolderControllerProps<T = Project> {
  filter: any;
  onProjectsLoaded?: (projects: T[]) => void;
  onDragProjects: (data: Project[], test: any) => void;
  selectable?: boolean;
  onSelect?: (projectId: string) => void;
  withoutGoToProject?: boolean;
  isParticipate?: string[];
  modal?: boolean;
  linkPrefix?: string;
  projectsCount?: number;
  isLoading?: Function;
  isSelectRightNeed?: boolean;
  columns?: number;
  isUnload?: boolean;
  preloadedTabs?: Tabs[];
  emptyMsgComponent?: ReactNode;
  currentTab: string;
  currentCategory: string;
  contextName?: string;
  isPageWithoutTabs?: boolean;
  emptyMsgText?: string;
  showCompetitionPoint?: boolean;
  isTeamProjects?: boolean;
  isDisabled?: boolean;
  canMakeImageForTeam?: TOwnerRights;
  isMainPage?: boolean;
  isProfile?: boolean;
  isPeoplePage?: boolean;
  isCompetitions?: boolean;
  handlers?: CombinedEvents;
  moderationType?: ModerationType;
  loading?: boolean;
  teamId?: string;
  mobileColumns?: number;
  scrollableTarget?: any;
  isMe?: number;
  scrollStyles?: any;
  showOpa?: boolean;
  isOpaCard?: boolean;
  isMobileMoreVisible?: boolean;

  folderId: string;
  canDrag?: boolean,
}

const ProjectsFolderController: FC<ProjectsFolderControllerProps> = ({
  filter,
  modal = false,
  onDragProjects,
  withoutGoToProject,
  linkPrefix,
  columns,
  showCompetitionPoint,
  canMakeImageForTeam,
  isProfile,
  isMainPage,
  handlers,
  mobileColumns,
  loading,
  folderId,
  canDrag,
}) => {
  const designer = useProfile();
  const { id: designerId } = usedUseParams(useParamsProps)
  const isMobile = useBreakpoint(LayoutConst.screen.s - 1);
  const dispatch = useDispatch();
  const isMedia = AppTester.isMediiia();

  const { i18n: { language } } = useTranslation();
  const [time, setTime] = useState(Date.now());

  const prevUserId = useRef<any>(null);
  const prevFilterRef = useRef<any>(null);

  const data = useSelector(ProfileFoldersSelector)?.data as PageModel<Project>;
  const fetchingTabs = useSelector(ProfileFoldersSelector)?.isLoading;
  const prevFolder = useSelector(ProfileFoldersSelector)?.prevFolder;

  const reloadAllTabs = useCallback(() => {
    setTime(Date.now());
    handlers?.onUpdate?.();
  }, [handlers])

  const equalRequest = useMemo(() => {
    return !!isEqual((filter?.projectIds || []), prevFilterRef.current)
  } , [filter, filter?.projectIds, prevFilterRef])

  useEffect(() => {
    if (isProfile && prevUserId.current && prevUserId.current !== designerId) {
      dispatch(ProfileFoldersSlice.actions.resetFolderProjects())
    }

    prevUserId.current = designerId
  } , [prevUserId, designerId, dispatch, isProfile])

  useEffect(() => {
    if (!equalRequest) {
      dispatch(fetchFolderData({ filter }))
    }

    prevFilterRef.current = filter?.projectIds

  }, [
    time,
    prevFilterRef,
    equalRequest,
    prevFolder,
    folderId,
    filter,
  ])

  useEffect(() => {
    return () => {
      dispatch(ProfileFoldersSlice.actions.setPrevFolder(folderId))
      dispatch(ProfileFoldersSlice.actions.resetFolderProjects())
    }
  }, [folderId])

  const translatedProjects = useMemo(() => {
    return data?.data ? API.toLang<Project[]>(data?.data, language) : []
  }, [data?.data, language]);

  return (
    <FlexBox offsetTop={24} offsetBottom={50} className={'folder-grid'} direction="column" directionMobile="column" gy={isMedia ? 0 : 40} flex="1">

        {/*@ts-ignore*/}
       {canDrag && !isMobile ? (
        <FoldersMove 
          projects={translatedProjects}
          // @ts-ignore
          onDragProjects={onDragProjects}
          canDrag={canDrag}
       />
       ) : (
        <ProjectsGridNew k={isMainPage ? 20 : undefined} isLoading={fetchingTabs} columns={columns} mobileColumns={mobileColumns}>
          {translatedProjects?.map((v: Nullable<Partial<Project<'Local'>>>, index: number) => {
            if (!v?.isBook) {
              return (
                // @ts-ignore
                <ProjectCardNew
                  project={v as any}
                  isModal={modal}
                  withoutGoToProject={withoutGoToProject}
                  onProjectClick={modal ?
                    (projectId?: string, e?: any) => {
                      e?.stopPropagation();
                      e?.preventDefault();
                    } : undefined}
                  isEditable={
                    ((v && designer && bProject.isEditableFor(v as any, designer)) ||
                      bProfile.isModer(designer as any) || bProfile.isMiddleModer(designer as any))
                      && !BookDelegate?.isPaid(v as any)
                  }
                  //@ts-ignore
                  isRejected={ProjectDelegate.isRejectedProject(v)}
                  key={(v?.projectId || '') + index}
                  {...computeProject(v)}
                  onShouldReload={reloadAllTabs}
                  linkPrefix={linkPrefix ? linkPrefix : undefined}
                  showCompetitionPoint={showCompetitionPoint}
                  canMakeImageForTeam={canMakeImageForTeam}
                />
              );
            } else {
              const book = computeBookProject(v);
              return (
                // @ts-ignore
                <BookCard
                  book={book as any}
                  key={(book?.bookId || '') + index}
                  {...computeBook(book)}
                  onShouldReload={reloadAllTabs}
                  linkPrefix={linkPrefix ? linkPrefix : undefined}
                />
              );
            }
          })}
        </ProjectsGridNew>
       )}
      <EmptyCardsLoading k={isMainPage ? 0 : 8} isLoading={fetchingTabs}/>
      <OffsetBlock display={true}/>
    </FlexBox>
  );
};

export default memo(ProjectsFolderController)